import React, { useEffect, useRef, useState } from 'react';
import './dropdown-menu.css';

/**
 * Menu déroulant générique (menus « ··· », boutons à chevron)
 *
 * @param {function} renderTrigger - ({ isOpen, toggle }) => élément déclencheur
 * @param {Array} items - { label, hint, onClick, disabled, danger, separator, chevron }
 * @param {node} children - contenu libre à la place de la liste d'options
 * @param {'left'|'right'} align - alignement horizontal du menu
 * @param {'down'|'up'} direction - ouverture vers le bas ou vers le haut
 * @param {string} footer - note affichée en bas du menu
 */
export default function DropdownMenu({
  renderTrigger,
  items = [],
  children,
  align = 'right',
  direction = 'down',
  width = 260,
  footer,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);
  const toggle = (e) => {
    if (e) e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="agv-dropdown" ref={containerRef}>
      {renderTrigger({ isOpen, toggle })}
      {isOpen && (
        <div
          className={`agv-menu agv-menu--${align} agv-menu--${direction}`}
          style={{ width }}
          onClick={(e) => e.stopPropagation()}
        >
          {children ? (
            children({ close })
          ) : (
            <div className="agv-menu__options">
              {items.map((item, index) =>
                item.separator ? (
                  <div key={`sep-${index}`} className="agv-menu__separator">
                    <div />
                  </div>
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    className={`agv-menu__option ${
                      item.danger ? 'agv-menu__option--danger' : ''
                    }`}
                    disabled={item.disabled}
                    onClick={() => {
                      close();
                      item.onClick();
                    }}
                  >
                    <span className="agv-menu__label">{item.label}</span>
                    <span className="agv-menu__spacer" />
                    {item.hint && (
                      <span className="agv-menu__hint">{item.hint}</span>
                    )}
                    {item.chevron && (
                      <span className="agv-menu__chevron">›</span>
                    )}
                  </button>
                )
              )}
            </div>
          )}
          {footer && <div className="agv-menu__foot">{footer}</div>}
        </div>
      )}
    </div>
  );
}
